import { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import IconButton from '@/shared/components/IconButton/IconButton'
import TextField from '@/shared/components/TextField/TextField'
import { ExtraBlock, SectionKey } from '../../schema/site'
import { extraItemId } from '../../utils/extrasOrder'
import { photoSlotId } from '../../utils/photos'
import PhotoField from '../PhotoField/PhotoField'
import TokenSelect from '../PropertiesPanel/TokenSelect'
import formStyles from '../SiteSettingsForm/SiteSettingsForm.module.scss'
import { sectionTitle } from './sectionTitle'
import styles from './SectionsEditor.module.scss'

/** The element id a block's card is found by, from the page and the preview. */
export const extraCardId = (id: string) => `website-extra-${id}`

type FieldEvent = ChangeEvent<HTMLInputElement | HTMLTextAreaElement>

interface ExtraCardProps {
  extra: ExtraBlock
  /** Its number among the blocks, for the card's title. */
  number: number
  language: string
  /** The sections a block may follow, in the template's order. */
  order: readonly SectionKey[]
  onChange: (values: Partial<ExtraBlock>) => void
  onRemove: () => void
}

/**
 * One of the owner's own blocks, as a card that can be dragged between the
 * section cards around it.
 *
 * Dragging is by the grip only, not by the card: a card full of fields and a
 * photo tile cannot also be a handle, or every press on a field would start
 * a drag. The grip is a keyboard target as well; dnd-kit's own keys move it.
 * The "after section" select stays for the precise version of the same move.
 */
function ExtraCard(props: ExtraCardProps) {
  const { extra, number, language, order, onChange, onRemove } = props
  const { t } = useTranslation()

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: extraItemId(extra.id) })

  const localized = (
    field: 'heading' | 'text'
  ): { value: string; onChange: (event: FieldEvent) => void } => ({
    value: extra[field]?.[language] ?? '',
    onChange: event =>
      onChange({ [field]: { ...extra[field], [language]: event.target.value } })
  })

  const id = extraCardId(extra.id)

  return (
    <div
      ref={setNodeRef}
      id={id}
      /*
        While it is in the air the card is the outline of the slot it will
        drop into: the fields inside it are hidden rather than unmounted, so
        the gap stays exactly as tall as the block, and the pointer carries a
        plate with its name on it instead of a form.
      */
      className={`${styles.card} ${isDragging ? styles.placeholder : ''}`}
      // Computed geometry, which is what an inline style is for: where the
      // card is while it is being dragged and while the others make room.
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <div className={styles.head}>
        <button
          ref={setActivatorNodeRef}
          type="button"
          className={styles.handle}
          aria-label={t('WEBSITE.EXTRAS.DRAG')}
          {...attributes}
          {...listeners}
        >
          <i className="las la-grip-vertical icon-medium" aria-hidden="true" />
        </button>
        <p className={`title-bold-small ${styles.title}`}>
          {t('WEBSITE.EXTRAS.BLOCK', { number })}
        </p>
        <IconButton
          icon="las la-trash"
          color="primary"
          ariaLabel={t('WEBSITE.EXTRAS.REMOVE')}
          onClick={onRemove}
        />
      </div>
      <PhotoField
        label={t('WEBSITE.PROPS.PHOTO')}
        slotId={photoSlotId(`extra-${extra.id}`)}
        ratio="4:3"
        value={extra.photo ?? ''}
        // The schema refuses an empty reference; no photo is no field.
        onChange={url => onChange({ photo: url || undefined })}
      />
      {/* Where it goes and what it is called, side by side (`pair`). */}
      <div className={formStyles.pair}>
        <TokenSelect
          label={t('WEBSITE.EXTRAS.AFTER')}
          value={extra.after}
          options={[...order]}
          translate={key => sectionTitle(key as SectionKey, language, t)}
          onChange={value => onChange({ after: value as SectionKey })}
        />
        <TextField
          label={t('WEBSITE.EXTRAS.HEADING')}
          focused
          {...localized('heading')}
        />
        {extra.photo && (
          <TokenSelect
            label={t('WEBSITE.EXTRAS.PHOTO_SIDE')}
            value={extra.photoSide}
            options={['left', 'right']}
            translate={value => t(`WEBSITE.EXTRAS.SIDE.${value}`)}
            onChange={value =>
              onChange({ photoSide: value as ExtraBlock['photoSide'] })
            }
          />
        )}
      </div>
      <TextField
        label={t('WEBSITE.EXTRAS.TEXT')}
        focused
        multiline
        lines={3}
        {...localized('text')}
      />
    </div>
  )
}

export default ExtraCard
